import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, 
  Brush, ReferenceArea, CartesianGrid 
} from "recharts";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LevelsChart = ({ sensors, fullscreen = false, compact = false }) => {
  const [period, setPeriod] = useState("day");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usingRealData, setUsingRealData] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 300, height: 200 });
  const containerRef = useRef(null);
  
  // Zoom state
  const [zoomDomain, setZoomDomain] = useState(null);
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  const [isZooming, setIsZooming] = useState(false);

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        if (width > 0 && height > 0) {
          setDimensions({ width: Math.floor(width), height: Math.floor(height - 50) });
        }
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const timer = setTimeout(updateDimensions, 100);
    const timer2 = setTimeout(updateDimensions, 500);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [fullscreen]);

  // Fetch ONLY real data from Modbus proxy - NO mock/demo data
  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/sensors/history/aggregated?range=${period}`);
        const data = response.data;
        
        // Only use real proxy data
        if (data && data.length >= 1) {
          setChartData(data);
          setUsingRealData(true);
        } else {
          // No data available - show empty chart
          setChartData([]);
          setUsingRealData(false);
        }
      } catch (err) {
        console.error("Error fetching history:", err);
        setChartData([]);
        setUsingRealData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [period, sensors]);

  const periods = [
    { key: "day", label: "Día" },
    { key: "week", label: "Semana" },
    { key: "month", label: "Mes" },
    { key: "year", label: "Año" },
  ];

  // Zoom functions
  const handleMouseDown = (e) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsZooming(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isZooming && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
      const leftIndex = chartData.findIndex(d => d.time === refAreaLeft);
      const rightIndex = chartData.findIndex(d => d.time === refAreaRight);
      
      if (leftIndex !== -1 && rightIndex !== -1) {
        const start = Math.min(leftIndex, rightIndex);
        const end = Math.max(leftIndex, rightIndex);
        setZoomDomain({ start, end });
      }
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
    setIsZooming(false);
  };

  const resetZoom = () => {
    setZoomDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  // Get zoomed data
  const getDisplayData = () => {
    if (!zoomDomain) return chartData;
    return chartData.slice(zoomDomain.start, zoomDomain.end + 1);
  };

  // Fullscreen version with larger chart and zoom
  if (fullscreen) {
    const displayData = getDisplayData();
    
    return (
      <div className="bg-[#141415] h-full flex flex-col p-6" data-testid="levels-chart-fullscreen">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="font-heading text-2xl uppercase tracking-wider text-zinc-100">
              Histórico de Niveles
            </span>
            {zoomDomain && (
              <Button
                size="sm"
                variant="outline"
                onClick={resetZoom}
                className="h-8 px-3 text-xs border-zinc-600 text-zinc-300 hover:bg-zinc-700"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset Zoom
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {periods.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={period === p.key ? "default" : "ghost"}
                className={`h-9 px-4 ${
                  period === p.key 
                    ? "bg-blue-600 text-white" 
                    : "text-zinc-400 hover:text-zinc-200 border border-zinc-700"
                }`}
                onClick={() => { setPeriod(p.key); resetZoom(); }}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
        
        <p className="text-xs text-zinc-500 mb-2">
          <ZoomIn className="w-3 h-3 inline mr-1" />
          Arrastra sobre el gráfico para hacer zoom
        </p>
        
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={displayData} 
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis 
                dataKey="time" 
                tick={{ fill: '#71717a', fontSize: 11 }}
                axisLine={{ stroke: '#3f3f46' }}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                tick={{ fill: '#71717a', fontSize: 12 }}
                axisLine={{ stroke: '#3f3f46' }}
                tickLine={false}
                domain={['auto', 'auto']}
                label={{ value: 'Metros', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 12 }}
                tickFormatter={(v) => `${v}m`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#18181b', 
                  border: '1px solid #27272a',
                  borderRadius: '8px',
                  fontSize: '14px',
                  padding: '12px'
                }}
                labelStyle={{ color: '#a1a1aa', fontWeight: 'bold', marginBottom: '8px' }}
                formatter={(value, name) => {
                  const colors = {
                    'PARRALILLO': '#22c55e',
                    'SIBERIO': '#eab308', 
                    'CAIDERO': '#3b82f6'
                  };
                  return [
                    <span style={{ color: colors[name] || '#fff' }}>
                      {typeof value === 'number' ? value.toFixed(2) : value} m
                    </span>,
                    name
                  ];
                }}
              />
              <Legend 
                verticalAlign="top"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: '14px' }}
              />
              
              {/* Zoom selection area */}
              {refAreaLeft && refAreaRight && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  strokeOpacity={0.3}
                  fill="#3b82f6"
                  fillOpacity={0.3}
                />
              )}
              
              <Line 
                type="linear" 
                dataKey="PARRALILLO" 
                stroke="#22c55e" 
                strokeWidth={3}
                dot={{ fill: '#22c55e', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 8, stroke: '#22c55e', strokeWidth: 2, fill: '#141415' }}
                name="PARRALILLO"
              />
              <Line 
                type="linear" 
                dataKey="SIBERIO" 
                stroke="#eab308" 
                strokeWidth={3}
                dot={{ fill: '#eab308', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 8, stroke: '#eab308', strokeWidth: 2, fill: '#141415' }}
                name="SIBERIO"
              />
              <Line 
                type="linear" 
                dataKey="CAIDERO" 
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 8, stroke: '#3b82f6', strokeWidth: 2, fill: '#141415' }}
                name="CAIDERO"
              />
              
              {/* Brush for zoom control */}
              {!zoomDomain && chartData.length > 10 && (
                <Brush 
                  dataKey="time" 
                  height={30} 
                  stroke="#3b82f6"
                  fill="#1f1f23"
                  travellerWidth={10}
                  onChange={(range) => {
                    if (range.startIndex !== undefined && range.endIndex !== undefined) {
                      setZoomDomain({ start: range.startIndex, end: range.endIndex });
                    }
                  }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Current levels summary */}
        <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-zinc-800">
          {sensors.map((sensor) => {
            const color = sensor.name === "PARRALILLO" ? "green" : sensor.name === "SIBERIO" ? "yellow" : "blue";
            return (
              <div key={sensor.id} className={`bg-zinc-900/50 rounded-lg p-4 border-l-4 border-${color}-500`}>
                <span className="text-sm text-zinc-500 uppercase">{sensor.name}</span>
                <div className="flex items-end gap-2 mt-1">
                  <span className={`text-3xl font-bold font-mono text-${color}-400`}>
                    {sensor.current_level?.toFixed(2)}
                  </span>
                  <span className="text-sm text-zinc-500 mb-1">metros</span>
                </div>
                <span className={`text-xs ${
                  sensor.status === 'normal' ? 'text-green-400' :
                  sensor.status === 'warning' ? 'text-amber-400' :
                  sensor.status === 'critical' ? 'text-red-400' :
                  sensor.status === 'overflow' ? 'text-red-500' : 'text-blue-400'
                }`}>
                  {sensor.status === 'normal' ? 'Normal' :
                   sensor.status === 'warning' ? 'Alerta' :
                   sensor.status === 'critical' ? 'Crítico' :
                   sensor.status === 'overflow' ? 'DESBORDE' : 'Bajo'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Normal compact version - Fixed height approach
  return (
    <div ref={containerRef} className="bg-[#0A0F1A] h-full w-full flex flex-col p-1 sm:p-2 border border-blue-900/50 rounded-lg">
      <div className="flex items-center justify-between mb-0.5 sm:mb-1">
        <div className="flex items-center gap-1 sm:gap-2">
          <span className="font-heading text-[8px] sm:text-xs uppercase tracking-wider text-zinc-300 font-bold">
            Histórico
          </span>
          {!usingRealData && (
            <span className="text-[7px] sm:text-[9px] text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded">
              Sim
            </span>
          )}
        </div>
        <div className="flex gap-0.5">
          {periods.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? "default" : "ghost"}
              className={`h-4 sm:h-5 px-1 sm:px-2 text-[8px] sm:text-[10px] ${
                period === p.key 
                  ? "bg-blue-600 text-white" 
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      
      <div className="flex-1 min-h-[120px]">
        {chartData.length > 0 ? (
          <LineChart 
            width={Math.max(dimensions.width - 5, 200)} 
            height={Math.max(dimensions.height, 100)} 
            data={chartData} 
            margin={{ top: 5, right: 5, left: 0, bottom: 2 }}
          >
            <XAxis 
              dataKey="time" 
              tick={{ fill: '#71717a', fontSize: 7 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis 
              tick={{ fill: '#71717a', fontSize: 7 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              domain={[
                dataMin => Math.floor(Math.min(dataMin, 30)),
                dataMax => Math.ceil(Math.max(dataMax, 80))
              ]}
              width={25}
              tickCount={4}
              tickFormatter={(v) => `${v}m`}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#18181b', 
                border: '1px solid #27272a',
                borderRadius: '4px',
                fontSize: '10px',
                padding: '8px'
              }}
              labelStyle={{ color: '#a1a1aa', marginBottom: '4px' }}
              formatter={(value, name) => {
                const colors = {
                  'PARRALILLO': '#22c55e',
                  'SIBERIO': '#eab308', 
                  'CAIDERO': '#3b82f6'
                };
                return [
                  <span style={{ color: colors[name] || '#fff' }}>
                    {typeof value === 'number' ? value.toFixed(2) : value} m
                  </span>,
                  name
                ];
              }}
            />
            <Line 
              type="monotone" 
              dataKey="PARRALILLO" 
              stroke="#22c55e" 
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            <Line 
              type="monotone" 
              dataKey="SIBERIO" 
              stroke="#eab308" 
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            <Line 
              type="monotone" 
              dataKey="CAIDERO" 
              stroke="#3b82f6" 
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <div className="flex items-center justify-center h-full text-zinc-500 text-xs">
            Cargando datos...
          </div>
        )}
      </div>
      
      <div className="flex justify-center gap-2 sm:gap-3 mt-0.5 sm:mt-1">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500" />
          <span className="text-[7px] sm:text-[10px] text-zinc-400">PAR</span>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-yellow-500" />
          <span className="text-[7px] sm:text-[10px] text-zinc-400">SIB</span>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-blue-500" />
          <span className="text-[7px] sm:text-[10px] text-zinc-400">CAI</span>
        </div>
      </div>
    </div>
  );
};

export default LevelsChart;
